// 되돌리기 어려운 동작 전에 묻는 **공용 확인 창**(2026-09-27 사용자 요청 — "세션 종료·로그아웃·데이터 삭제는 확인 받고 진행").
//
// 왜 `window.confirm` 이 아닌가: 통합앱의 macOS 웹뷰(WKWebView)는 페이지가 띄우는 기본 대화상자를 앱이 따로 받아 주지 않으면
// **창 없이 곧바로 "취소"** 로 끝날 수 있다 — 삭제 버튼이 조용히 안 먹는 것처럼 보인다. 화면 안에 직접 그리는 창은 어디서나 같게 뜬다.
//
// 쓰는 법: 각 창의 뿌리 컴포넌트에 `<ConfirmHost />` 를 한 번 두고, 어디서든 `if (!(await confirmAction({...}))) return;`.
import { useEffect, useRef, useState } from 'react';

export interface ConfirmOptions {
  /** 한 줄 질문(예: "로그아웃할까요?"). */
  title: string;
  /** 무엇이 사라지거나 끊기는지 — 결과를 구체적으로. */
  message?: string;
  /** 진행 단추 문구(동사로 — "삭제", "연결 종료"). 기본 "확인". */
  confirmLabel?: string;
  /** 진행 단추를 위험 색으로(삭제·해제). */
  danger?: boolean;
  /**
   * 이 글자를 **똑같이** 입력해야 진행 단추가 켜진다(되돌리기 어려운 삭제 — 예: 조직 이름, §14.19.10).
   * 앞뒤 공백은 무시하고 대소문자는 구분한다. 진행하면 입력 칸에 포커스가 먼저 간다.
   */
  requireText?: string;
  /** 취소 단추를 숨긴다 — 알리기만 하는 창(`alertAction`). */
  hideCancel?: boolean;
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

/** 지금 화면에 붙어 있는 확인 창 표시기(창마다 하나). */
let show: ((p: Pending) => void) | null = null;

/**
 * 확인을 받는다 — 진행이면 true. 표시기가 없는 창에서는(붙이는 것을 잊음) 기본 대화상자로 대신한다.
 */
export function confirmAction(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (show) {
      show({ ...opts, resolve });
    } else {
      resolve(window.confirm([opts.title, opts.message].filter(Boolean).join('\n\n')));
    }
  });
}

/**
 * 문장 하나로 묻는다 — **첫 문단(빈 줄 앞) = 질문, 나머지 = 설명**. 종전 `window.confirm(문장)` 을 문구 그대로 옮길 때 쓴다.
 * @param danger 진행 단추를 위험 색으로(기본 true — 이런 확인은 대개 차단·삭제다).
 */
export function confirmText(text: string, confirmLabel?: string, danger = true): Promise<boolean> {
  const [title, ...rest] = text.split('\n\n');
  return confirmAction({ title, message: rest.join('\n\n') || undefined, confirmLabel, danger });
}

/**
 * 알리기만 한다(단추 하나) — 진행할 수 없는 이유를 보여 줄 때(예: "단말기를 모두 제거한 뒤 다시"). 닫히면 끝난다.
 */
export async function alertAction(title: string, message?: string): Promise<void> {
  await confirmAction({ title, message, confirmLabel: '확인', hideCancel: true });
}

/** 입력이 요구 글자와 같은가(앞뒤 공백 무시 · 대소문자 구분 — 서버 대조와 같은 규칙). */
function textMatches(typed: string, required: string): boolean {
  return typed.trim() === required.trim();
}

/** 확인 창 표시기 — 각 창의 뿌리에 한 번. Esc = 취소, Enter = 진행. 바깥을 눌러도 취소. */
export function ConfirmHost() {
  const [cur, setCur] = useState<Pending | null>(null);
  const [typed, setTyped] = useState('');
  const okRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    show = (p) =>
      setCur((prev) => {
        prev?.resolve(false); // 앞의 것이 떠 있으면 취소로 닫고 새것을 띄운다
        return p;
      });
    return () => {
      show = null;
    };
  }, []);

  // 위험 동작은 **취소**에 먼저 포커스 — Enter 를 습관적으로 눌러 지워지지 않게.
  // 입력 확인 창은 입력 칸에(맞게 입력하기 전엔 진행 단추가 꺼져 있어 Enter 로 지워지지 않는다).
  useEffect(() => {
    if (!cur) return;
    setTyped('');
    if (cur.requireText !== undefined) inputRef.current?.focus();
    else (cur.danger && !cur.hideCancel ? cancelRef : okRef).current?.focus();
  }, [cur]);

  if (!cur) return null;
  const canProceed = cur.requireText === undefined || textMatches(typed, cur.requireText);
  const close = (ok: boolean) => {
    if (ok && !canProceed) return;
    cur.resolve(ok);
    setCur(null);
  };
  return (
    <div
      className="confirm-backdrop"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && close(false)}
      onKeyDown={(e) => {
        e.stopPropagation(); // 원격 화면으로 키가 새지 않게(세션 창)
        if (e.key === 'Escape') close(false);
      }}
    >
      <div className="confirm-box" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <div id="confirm-title" className="confirm-title">
          {cur.title}
        </div>
        {cur.message && <div className="confirm-msg">{cur.message}</div>}
        {cur.requireText !== undefined && (
          <label className="confirm-require">
            <span>
              확인을 위해 <b>{cur.requireText}</b> 을(를) 입력하세요.
            </span>
            <input
              ref={inputRef}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') close(true); // 맞지 않으면 close 가 무시한다
              }}
              autoComplete="off"
              spellCheck={false}
              aria-label="확인 입력"
            />
          </label>
        )}
        <div className="confirm-actions">
          {!cur.hideCancel && (
            <button ref={cancelRef} className="ghost" onClick={() => close(false)}>
              취소
            </button>
          )}
          <button
            ref={okRef}
            className={cur.danger ? 'danger-solid' : undefined}
            onClick={() => close(true)}
            disabled={!canProceed}
          >
            {cur.confirmLabel ?? '확인'}
          </button>
        </div>
      </div>
    </div>
  );
}
